import {
    Autocomplete, Box, Card, CardHeader, IconButton, Stack, TextField, Tooltip, Typography
} from '@mui/material'
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import { RemoveCircleOutline, FilterAltOff, Search } from '@mui/icons-material'
import CachedIcon from '@mui/icons-material/Cached'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { EntityWithIdAndDescriptionFields } from 'src/types'
import { WrapperTable } from '../components'
import { HttpLCTCRelacion, HttpClienteTipoLCTC, HttpClienteSubTipoLCTC } from './api'
import { HttpSectorComercialABM } from '../sectores-comerciales/api'
import { RelacionLCTCViewDTO, RelacionLCTCFields, RelacionLCTCItemDTO, ClienteTipoLCTCDTO, ClienteSubTipoLCTCDTO, ClienteTipoLCTCFields, ClienteSubTipoLCTCFields } from './types'
import { Controller, useForm } from 'react-hook-form'

type FilterForm = { codPais: any; codSectorComercial: any }

function ConfigRelacionesLCTC() {
    const { paisLogos } = usePaisLogo()
    const [relaciones, setRelaciones] = useState<RelacionLCTCViewDTO[]>([])
    const [sectores, setSectores] = useState<{ id: number; descripcion: string }[]>([])
    const [tipos, setTipos] = useState<ClienteTipoLCTCDTO[]>([])
    const [subtipos, setSubtipos] = useState<ClienteSubTipoLCTCDTO[]>([])
    const [nuevoTipo, setNuevoTipo] = useState<ClienteTipoLCTCDTO | null>(null)
    const [nuevoSubtipo, setNuevoSubtipo] = useState<ClienteSubTipoLCTCDTO | null>(null)

    const { control, watch, setValue } = useForm<FilterForm>({ defaultValues: { codPais: null, codSectorComercial: null } })
    const selectedPais = watch('codPais')
    const selectedSector = watch('codSectorComercial')

    useEffect(() => {
        HttpClienteTipoLCTC.getAll().then(setTipos)
        HttpClienteSubTipoLCTC.getAll().then(setSubtipos)
    }, [])

    useEffect(() => {
        if (!selectedPais) { setSectores([]); setValue('codSectorComercial', null); return }
        HttpSectorComercialABM.getSectoresComerciales().then(data => {
            const filtered = data
                .filter(s => s.codPais === (selectedPais?.id ?? selectedPais))
                .map(s => ({ id: s.id, descripcion: `${s.nombreUnidadNegocio ?? ''} - ${s.nombreSectorComercial}` }))
                .sort((a, b) => a.descripcion.localeCompare(b.descripcion))
            setSectores(filtered)
            setValue('codSectorComercial', null)
        })
    }, [selectedPais])

    useEffect(() => {
        if (!selectedSector) { setRelaciones([]); return }
        const codSector = selectedSector?.id ?? selectedSector
        HttpLCTCRelacion.getBySector(codSector).then(setRelaciones)
    }, [selectedSector])

    const refetch = () => {
        const codSector = selectedSector?.id ?? selectedSector
        if (codSector) HttpLCTCRelacion.getBySector(codSector).then(setRelaciones)
    }

    const handleRemove = (rel: RelacionLCTCViewDTO) => {
        setRelaciones(prev => prev.filter(r => r[RelacionLCTCFields.Id] !== rel[RelacionLCTCFields.Id]))
    }

    const handleAdd = () => {
        if (!nuevoTipo || !nuevoSubtipo) return
        const exists = relaciones.some(
            r => r[RelacionLCTCFields.CodClienteTipo] === nuevoTipo.id &&
                 r[RelacionLCTCFields.CodClienteSubTipo] === nuevoSubtipo.id
        )
        if (exists) { toast.error('La relación ya existe'); return }

        const codSector = selectedSector?.id ?? selectedSector
        setRelaciones(prev => [...prev, {
            [RelacionLCTCFields.Id]: -(prev.length + 1),
            [RelacionLCTCFields.CodSectorComercial]: codSector,
            [RelacionLCTCFields.NombreSectorComercial]: '',
            [RelacionLCTCFields.CodClienteTipo]: nuevoTipo.id,
            [RelacionLCTCFields.DescripcionClienteTipo]: nuevoTipo[ClienteTipoLCTCFields.Descripcion],
            [RelacionLCTCFields.CodClienteSubTipo]: nuevoSubtipo.id,
            [RelacionLCTCFields.DescripcionClienteSubTipo]: nuevoSubtipo[ClienteSubTipoLCTCFields.Descripcion],
            [RelacionLCTCFields.Activo]: true
        }])
        setNuevoTipo(null)
        setNuevoSubtipo(null)
    }

    const handleSave = () => {
        const codSector = selectedSector?.id ?? selectedSector
        if (!codSector) return Promise.resolve()
        const items: RelacionLCTCItemDTO[] = relaciones.map(r => ({
            codClienteTipo: r[RelacionLCTCFields.CodClienteTipo],
            codClienteSubTipo: r[RelacionLCTCFields.CodClienteSubTipo]
        }))
        const promise = HttpLCTCRelacion.update(codSector, items).then(() => refetch())
        toast.promise(promise, { success: 'Relaciones actualizadas', error: 'Error al guardar', loading: 'Guardando' })
        return promise
    }

    const columnas: GridColDef<RelacionLCTCViewDTO>[] = [
        { headerName: 'Tipo de Cliente', field: RelacionLCTCFields.DescripcionClienteTipo, flex: 2 },
        { headerName: 'Subtipo de Cliente', field: RelacionLCTCFields.DescripcionClienteSubTipo, flex: 2 },
        {
            field: 'actions', type: 'actions', width: 60,
            getActions: ({ row }) => [
                <GridActionsCellItem
                    icon={<Tooltip title='Quitar'><RemoveCircleOutline fontSize='small' /></Tooltip>}
                    label='Quitar' onClick={() => handleRemove(row)} color='error'
                />
            ]
        }
    ]

    const codSector = selectedSector?.id ?? selectedSector

    return (
        <WrapperTable>
            <Card>
                <CardHeader
                    title='Configuración de Relaciones LCTC'
                    subheader='Sector Comercial - Tipo Cliente - Subtipo Cliente'
                    action={
                        <Stack direction='row' spacing={1}>
                            {codSector && (<>
                                <IconButton onClick={refetch}><CachedIcon /></IconButton>
                                <Button variant='contained' color='primary' promise={handleSave}>Guardar</Button>
                            </>)}
                        </Stack>
                    }
                />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mx: 2, mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Box sx={{ minWidth: 200, flex: 1 }}>
                        <Controller control={control} name='codPais'
                            render={({ field }) => (
                                <Autocomplete
                                    options={paisLogos.map(s => ({ id: s.id, descripcion: s[EntityWithIdAndDescriptionFields.Description] }))}
                                    value={field.value} onChange={(_, v) => field.onChange(v)}
                                    getOptionLabel={(o: any) => o.descripcion ?? ''}
                                    isOptionEqualToValue={(o: any, v: any) => o.id === v?.id}
                                    size='small' renderInput={params => <TextField {...params} label='País *' />}
                                />
                            )}
                        />
                    </Box>
                    <Box sx={{ minWidth: 200, flex: 1 }}>
                        <Controller control={control} name='codSectorComercial'
                            render={({ field }) => (
                                <Autocomplete
                                    disabled={!selectedPais} options={sectores}
                                    value={field.value} onChange={(_, v) => field.onChange(v)}
                                    getOptionLabel={(o: any) => o.descripcion ?? ''}
                                    isOptionEqualToValue={(o: any, v: any) => o.id === v?.id}
                                    size='small' renderInput={params => <TextField {...params} label='Sector Comercial *' />}
                                />
                            )}
                        />
                    </Box>
                </Box>

                {codSector && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mx: 2, mb: 2, p: 2, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                        <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>Agregar:</Typography>
                        <Autocomplete options={tipos} value={nuevoTipo} onChange={(_, v) => setNuevoTipo(v)}
                            getOptionLabel={(o) => o[ClienteTipoLCTCFields.Descripcion]}
                            isOptionEqualToValue={(o, v) => o.id === v?.id}
                            size='small' sx={{ flex: 1 }}
                            renderInput={params => <TextField {...params} label='Tipo de Cliente' />}
                        />
                        <Autocomplete options={subtipos} value={nuevoSubtipo} onChange={(_, v) => setNuevoSubtipo(v)}
                            getOptionLabel={(o) => o[ClienteSubTipoLCTCFields.Descripcion]}
                            isOptionEqualToValue={(o, v) => o.id === v?.id}
                            size='small' sx={{ flex: 1 }}
                            renderInput={params => <TextField {...params} label='Subtipo de Cliente' />}
                        />
                        <Button variant='outlined' color='primary' size='small' onClick={handleAdd} disabled={!nuevoTipo || !nuevoSubtipo}>
                            Agregar
                        </Button>
                    </Box>
                )}

                {codSector ? (
                    <Box sx={{ mx: 2, mb: 2 }}>
                        <DataGrid disableColumnFilter disableColumnMenu columns={columnas} rows={relaciones}
                            getRowId={row => row[RelacionLCTCFields.Id]} density='compact' pagination
                            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                            pageSizeOptions={[25, 50, 75, 100]}
                        />
                    </Box>
                ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color='text.secondary'>Seleccione un país y sector comercial para ver las relaciones</Typography>
                    </Box>
                )}
            </Card>
        </WrapperTable>
    )
}

export default ConfigRelacionesLCTC
